import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import { Eye, EyeOff } from "lucide-react";
import Logo from "../assets/Logo1.png";

const Login = () => {
  const [showPassword, setShowPassword] = useState(false);
  const [username, setUsername] = useState("");
  const [password, setPassword] = useState("");
  const [showSuccess, setShowSuccess] = useState(false);
  const [errors, setErrors] = useState({ username: "", password: "" });
  const navigate = useNavigate();

  const handleSubmit = (e) => {
    e.preventDefault();
    const newErrors = { username: "", password: "" };
    let isValid = true;

    // Username validation
    if (!username.trim()) {
      newErrors.username = "Username is required.";
      isValid = false;
    } else if (username.length < 3) {
      newErrors.username = "Username must be at least 3 characters.";
      isValid = false;
    } else if (!/^[a-zA-Z0-9_]+$/.test(username)) {
      newErrors.username = "Username can only contain letters, numbers, and underscores.";
      isValid = false;
    }

    // Password validation
    if (!password.trim()) {
      newErrors.password = "Password is required.";
      isValid = false;
    } else if (password.length < 8) {
      newErrors.password = "Password must be at least 8 characters.";
      isValid = false;
    } else if (!/[A-Z]/.test(password)) {
      newErrors.password = "Password must contain at least one uppercase letter.";
      isValid = false;
    } else if (!/[a-z]/.test(password)) {
      newErrors.password = "Password must contain at least one lowercase letter.";
      isValid = false;
    } else if (!/[0-9]/.test(password)) {
      newErrors.password = "Password must contain at least one number.";
      isValid = false;
    }

    setErrors(newErrors);

    if (isValid) {
      setShowSuccess(true);
      setTimeout(() => {
        navigate("/Dashboard");
      }, 1500); // Navigate after showing success message for 1.5 seconds
    }
  };

  return (
    <>
      <style>{`
                @keyframes gradient {
                    0% { background-position: 0% 50%; }
                    50% { background-position: 100% 50%; }
                    100% { background-position: 0% 50%; }
                }
                body {
                    background: linear-gradient(45deg, rgba(238, 119, 82,0.2), rgba(231, 60, 126,0.2), rgba(35, 166, 213,0.2), rgba(35, 213, 171,0.2));
                    background-size: 400% 400%;
                    animation: gradient 15s ease infinite;
                    height: 100vh;
                }
            `}</style>
      <div className="relative overflow-hidden h-screen">
        {/* Success Message Popup */}
        {showSuccess && (
          <div className="fixed top-4 right-4 z-50 animate-fade-in">
            <div className="bg-[#635BFF] text-white px-4 py-2 rounded-lg shadow-lg flex items-center">
              <span className="material-symbols-outlined mr-2">check_circle</span>
              Login successful! Redirecting...
            </div>
          </div>
        )}
        <div className="flex h-full justify-center items-center px-4">
          <div className="rounded-xl shadow-md bg-white p-8 w-full md:w-[480px] border-none">
            <div className="flex flex-col gap-2 p-0 w-full">
              <div className="mb-2 flex items-center justify-center">
                <img src={Logo} alt="Company Logo" className="h-20 md:h-24" />
              </div>
              <p className="text-sm text-center text-gray-600 my-3">
                Sign In on Partner Portal
              </p>
              <form onSubmit={handleSubmit} className="w-full" autoComplete="off">
                <div className="mb-4">
                  <label
                    htmlFor="username"
                    className="block mb-2 text-sm font-medium"
                  >
                    Username<span className="text-red-500">*</span>
                  </label>
                  <input
                    type="text"
                    id="username"
                    value={username}
                    onChange={(e) => setUsername(e.target.value)}
                    className={`w-full p-2 border rounded-lg focus:outline-none focus:ring-2 ${
                      errors.username ? 'border-[#DC2626] focus:ring-[#DC2626]' : 'focus:ring-[#635BFF]'
                  }`} 
                  />
                  {errors.username && (
                    <p className="text-red-500 text-xs mt-1">
                      {errors.username}
                    </p>
                  )}
                </div>
                <div className="mb-4">
                  <label
                    htmlFor="password"
                    className="block mb-2 text-sm font-medium"
                  >
                    Password<span className="text-red-500">*</span>
                  </label>
                  <div className="relative">
                    <input
                      type={showPassword ? "text" : "password"}
                      value={password}
                      onChange={(e) => setPassword(e.target.value)}
                      className={`w-full p-2 border rounded-lg focus:outline-none focus:ring-2 ${
                        errors.password ? 'border-[#DC2626] focus:ring-[#DC2626]' : 'focus:ring-[#635BFF]'
                    }`} 
                    />
                    <span
                      className="absolute right-3 top-3 cursor-pointer text-gray-600 dark:text-gray-400"
                      onClick={() => setShowPassword(!showPassword)}
                    >
                      {showPassword ? <EyeOff size={18} /> : <Eye size={18} />}
                    </span>
                  </div>
                  {errors.password && (
                    <p className="text-red-500 text-xs mt-1">
                      {errors.password}
                    </p>
                  )}
                </div>
                <div className="flex justify-between my-5">
                  <label className="flex items-center gap-2">
                    <input
                      type="checkbox"
                      id="remember"
                      name="remember"
                      className="checkbox"
                    />
                    <span className="opacity-90 text-sm cursor-pointer">
                      Remember this Device
                    </span>
                  </label>
                  <a href="#" className="text-[#6B65FF] text-sm font-medium">
                    Forgot Password?
                  </a>
                </div>
                <button
                  type="submit"
                  className="w-full bg-[#6B65FF] text-white rounded-xl p-2"
                >
                  Sign in
                </button>
              </form>
              <div className="flex gap-2 text-base text-gray-600 font-medium mt-6 items-center justify-center">
                <p>New to Partner?</p>
                <a
                  href="/Register"
                  className="text-[#6B65FF] text-sm font-medium"
                >
                  Create an account
                </a>
              </div>
            </div>
          </div>
        </div>
      </div>
    </>
  );
};

export default Login;